import { useEffect, useRef, useState } from 'react';
import {
  ChevronLeft,
  Mail,
  Phone as PhoneIcon,
} from 'lucide-react';
import {
  admin,
  palette,
  radii,
  shadow,
  typography,
} from '../../../../styles/tokens';
import { normalizeEmail, normalizeName, normalizePhone } from '../shared/normalize';
import { useImportClients } from '../shared/useImportClients';
import {
  Banner,
  InlineButton,
  RevealButton,
  backButtonStyle,
  inputStyle,
} from './parts';
import RowItem, { type ConversationalRow } from './RowItem';

interface ConversationalPathProps {
  onBackToGrid: () => void;
  onSaved: () => void;
  onSwitchToSpreadsheet: () => void;
  onUnsavedCountChange?: (count: number) => void;
}

// Banner rules — fire at EXACTLY 15 and 50, silent past 100. Each has its
// own dismiss flag so dismissing the 15 banner does not suppress the 50.
const BANNER_15_AT = 15;
const BANNER_50_AT = 50;
const BANNER_SILENT_PAST = 100;

export default function ConversationalPath({
  onBackToGrid,
  onSaved,
  onSwitchToSpreadsheet,
  onUnsavedCountChange,
}: ConversationalPathProps) {
  const { insertClient, findPhoneMatch, deleteClient } = useImportClients();

  const [rows, setRows] = useState<ConversationalRow[]>([]);
  const [nameDraft, setNameDraft] = useState('');
  const [phoneDraft, setPhoneDraft] = useState('');
  const [emailDraft, setEmailDraft] = useState('');
  const [showPhone, setShowPhone] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [banner15Dismissed, setBanner15Dismissed] = useState(false);
  const [banner50Dismissed, setBanner50Dismissed] = useState(false);
  const [phoneBlurWarning, setPhoneBlurWarning] = useState<
    { existingName: string } | null
  >(null);
  const [switchingToSpreadsheet, setSwitchingToSpreadsheet] = useState(false);

  // Auto-dismiss the duplicate-phone warning after 5s if the user hasn't
  // taken an action — banner stays visible long enough to read but doesn't
  // linger past the typing context.
  useEffect(() => {
    if (!phoneBlurWarning) return;
    const t = setTimeout(() => setPhoneBlurWarning(null), 5000);
    return () => clearTimeout(t);
  }, [phoneBlurWarning]);

  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const rowSeq = useRef(0);

  // Banner-count fix: count CONFIRMED-SAVED rows only, not saving-in-flight.
  // The tiered banners are transition-moment nudges; firing mid-flight would
  // feel premature.
  const count = rows.filter(r => r.state === 'saved').length;
  const savedCount = rows.filter(r => r.state === 'saved').length;
  const hasInFlight = rows.some(r => r.state === 'saving');

  // C-020 (Q7): bubble truthful discard-count to parent so the skip-confirm
  // dialog (rendered at index level, where SkipLink lives) can gate accurately.
  // unsavedCount = rows that won't survive a skip — saving-in-flight or failed.
  const unsavedCount = rows.length - savedCount;
  useEffect(() => {
    onUnsavedCountChange?.(unsavedCount);
  }, [unsavedCount, onUnsavedCountChange]);

  const showBanner15 =
    count === BANNER_15_AT &&
    !banner15Dismissed &&
    count < BANNER_SILENT_PAST;
  const showBanner50 =
    count === BANNER_50_AT &&
    !banner50Dismissed &&
    count < BANNER_SILENT_PAST;

  const resetDraft = () => {
    setNameDraft('');
    setPhoneDraft('');
    setEmailDraft('');
    setPhoneBlurWarning(null);
    nameInputRef.current?.focus();
  };

  const handleAdd = async (bypassWarning = false) => {
    const name = normalizeName(nameDraft);
    if (!name) return;
    if (phoneBlurWarning && !bypassWarning) return;

    const rowKey = `conv-${rowSeq.current++}`;
    const phone = normalizePhone(phoneDraft);
    const email = normalizeEmail(emailDraft);

    const optimistic: ConversationalRow = {
      rowKey,
      clientId: null,
      name,
      phone,
      email,
      state: 'saving',
    };
    setRows(prev => [...prev, optimistic]);
    resetDraft();

    const result = await insertClient({
      name,
      phone: phone || undefined,
      email: email || undefined,
      source: 'conversational',
    });

    setRows(prev =>
      prev.map(r => {
        if (r.rowKey !== rowKey) return r;
        if ('error' in result) {
          return { ...r, state: 'failed', errorMessage: result.error };
        }
        return { ...r, state: 'saved', clientId: result.id };
      }),
    );
  };

  const handleRetry = async (rowKey: string) => {
    const row = rows.find(r => r.rowKey === rowKey);
    if (!row) return;
    setRows(prev =>
      prev.map(r =>
        r.rowKey === rowKey ? { ...r, state: 'saving', errorMessage: undefined } : r,
      ),
    );
    const result = await insertClient({
      name: row.name,
      phone: row.phone || undefined,
      email: row.email || undefined,
      source: 'conversational',
    });
    setRows(prev =>
      prev.map(r => {
        if (r.rowKey !== rowKey) return r;
        if ('error' in result) {
          return { ...r, state: 'failed', errorMessage: result.error };
        }
        return { ...r, state: 'saved', clientId: result.id };
      }),
    );
  };

  const handleRemove = async (rowKey: string) => {
    const row = rows.find(r => r.rowKey === rowKey);
    if (!row) return;
    if (row.state === 'saved' && row.clientId) {
      // Optimistically remove locally; if the delete fails, reinsert.
      setRows(prev => prev.filter(r => r.rowKey !== rowKey));
      const result = await deleteClient(row.clientId);
      if ('error' in result) {
        setRows(prev => [
          ...prev,
          {
            ...row,
            state: 'failed',
            errorMessage: `Delete failed: ${result.error}`,
          },
        ]);
      }
    } else {
      setRows(prev => prev.filter(r => r.rowKey !== rowKey));
    }
  };

  const handlePhoneBlur = async () => {
    const phone = normalizePhone(phoneDraft);
    if (!phone) {
      setPhoneBlurWarning(null);
      return;
    }
    // Targeted single-phone lookup (Phase 4) — replaces a full findDuplicates
    // pass. One round-trip, one row, no client-side classifier.
    const match = await findPhoneMatch(phone);
    if (match) {
      setPhoneBlurWarning({ existingName: match.existingClientName });
    } else {
      setPhoneBlurWarning(null);
    }
  };

  const handleSwitchToSpreadsheet = () => {
    if (switchingToSpreadsheet) return;
    setSwitchingToSpreadsheet(true);
    onSwitchToSpreadsheet();
  };

  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  const canSubmit =
    normalizeName(nameDraft).length > 0 && !phoneBlurWarning;
  const doneDisabled =
    rows.length === 0 || hasInFlight || switchingToSpreadsheet;

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '8px 4px 0' }}>
      <button type="button" onClick={onBackToGrid} style={backButtonStyle}>
        <ChevronLeft size={16} />
        <span>Import your clients</span>
      </button>

      <h1
        style={{
          margin: 0,
          fontSize: typography.size.title,
          fontWeight: typography.weight.bold,
          color: admin.header,
          letterSpacing: '-0.01em',
          marginBottom: 8,
        }}
      >
        Add your clients one at a time
      </h1>
      <p
        style={{
          margin: 0,
          marginBottom: 20,
          fontSize: typography.size.body,
          color: admin.body,
          lineHeight: typography.lineHeight.relaxed,
        }}
      >
        Just the names you work with. You can fill in the details later.
      </p>

      {showBanner15 && (
        <Banner
          copy="This is a lot to type one at a time. Uploading a spreadsheet would be much faster. Want to switch?"
          onSwitch={handleSwitchToSpreadsheet}
          onDismiss={() => setBanner15Dismissed(true)}
        />
      )}
      {showBanner50 && (
        <Banner
          copy="50 clients is a real list. Typing the rest one at a time works, but uploading a spreadsheet would be much faster. Want to switch?"
          onSwitch={handleSwitchToSpreadsheet}
          onDismiss={() => setBanner50Dismissed(true)}
        />
      )}

      <form
        onSubmit={e => {
          e.preventDefault();
          void handleAdd();
        }}
        style={{
          padding: 16,
          background: palette.white,
          border: `1px solid ${palette.g150}`,
          borderRadius: radii.md,
          boxShadow: shadow.xs,
          marginBottom: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            ref={nameInputRef}
            value={nameDraft}
            onChange={e => setNameDraft(e.target.value)}
            placeholder="Client name"
            style={{ ...inputStyle, flex: '1 1 220px' }}
          />
          {!showPhone && (
            <RevealButton onClick={() => setShowPhone(true)} icon={<PhoneIcon size={14} />}>
              + phone
            </RevealButton>
          )}
          {!showEmail && (
            <RevealButton onClick={() => setShowEmail(true)} icon={<Mail size={14} />}>
              + email
            </RevealButton>
          )}
        </div>

        {showPhone && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <input
              value={phoneDraft}
              onChange={e => {
                setPhoneDraft(e.target.value);
                if (phoneBlurWarning) setPhoneBlurWarning(null);
              }}
              onBlur={handlePhoneBlur}
              placeholder="Phone (optional)"
              style={inputStyle}
            />
            {phoneBlurWarning && (
              <div
                style={{
                  padding: 10,
                  background: palette.amberWash,
                  border: `1px solid ${palette.amber}`,
                  borderRadius: radii.sm,
                  fontSize: typography.size.caption,
                  color: admin.body,
                  lineHeight: typography.lineHeight.relaxed,
                }}
              >
                You already have <strong>{phoneBlurWarning.existingName}</strong>{' '}
                with this number. Add anyway?
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <InlineButton onClick={() => handleAdd(true)}>
                    Add anyway
                  </InlineButton>
                  <InlineButton
                    onClick={() => {
                      setPhoneDraft('');
                      setPhoneBlurWarning(null);
                    }}
                    muted
                  >
                    Cancel
                  </InlineButton>
                </div>
              </div>
            )}
          </div>
        )}

        {showEmail && (
          <input
            value={emailDraft}
            onChange={e => setEmailDraft(e.target.value)}
            placeholder="Email (optional)"
            type="email"
            style={inputStyle}
          />
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              padding: '8px 18px',
              background: canSubmit ? palette.accent : palette.g300,
              color: palette.white,
              border: 'none',
              borderRadius: radii.sm,
              fontSize: typography.size.body,
              fontWeight: typography.weight.semibold,
              fontFamily: 'inherit',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            Add
          </button>
        </div>
      </form>

      <div style={{ marginBottom: 8 }}>
        <div
          style={{
            fontSize: typography.size.caption,
            color: admin.caption,
            marginBottom: 8,
          }}
        >
          {rows.length === 0
            ? 'Your list will appear here as you add clients.'
            : `${savedCount} saved${
                rows.length !== savedCount
                  ? ` · ${rows.length - savedCount} in flight or failed`
                  : ''
              }`}
        </div>

        {rows.length > 0 && (
          <div
            style={{
              border: `1px solid ${palette.g150}`,
              borderRadius: radii.md,
              background: palette.white,
              boxShadow: shadow.xs,
              maxHeight: 360,
              overflowY: 'auto',
            }}
          >
            {rows.map(row => (
              <RowItem
                key={row.rowKey}
                row={row}
                onRetry={() => handleRetry(row.rowKey)}
                onRemove={() => handleRemove(row.rowKey)}
              />
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 24,
          paddingBottom: 32,
        }}
      >
        <button
          type="button"
          onClick={onSaved}
          disabled={doneDisabled}
          style={{
            padding: '10px 24px',
            background: doneDisabled ? palette.g300 : palette.accent,
            color: palette.white,
            border: 'none',
            borderRadius: radii.sm,
            fontSize: typography.size.body,
            fontWeight: typography.weight.semibold,
            fontFamily: 'inherit',
            cursor: doneDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
}
